// Funding / Scholarship Pages (Tier 8) - cautious, non-guaranteeing language throughout
const base = {
  category: 'Funding / Scholarship Page',
  tier: 'Tier 8',
  cta: 'Ask About Funding Options',
  funding: true,
};

const pages = [
  {
    ...base,
    slug: '/recovery-housing-funding-assistance/',
    name: 'Recovery Housing Funding & Scholarship Assistance',
    primaryKeyword: 'Recovery Housing Funding Assistance',
    keywords: ['recovery housing scholarships', 'sober living payment assistance', 'recovery house funding', 'help paying for recovery housing'],
    title: 'Recovery Housing Funding & Scholarship Assistance | PGSRH',
    description: 'Need help paying for recovery housing? PGSRH can discuss scholarships, county/state funding, and payment assistance that may be available depending on eligibility. Ask us.',
    blurb: 'The main hub for funding, scholarship, and payment assistance questions.',
    heroSubtitle: 'Cost shouldn\u2019t be the last word on recovery. Learn what funding, scholarship, and assistance options may be available \u2014 and how to ask.',
    fundingHeading: 'How funding and scholarships may work',
    intro: [
      'This is the place to start if you\u2019re worried about paying for recovery housing. PGSRH can talk with you about scholarships, county and state funding, and other payment assistance that may be available depending on eligibility and current availability.',
      'We keep this simple and honest. We do not promise free housing or guaranteed approval. What we can do is explain the options and help you understand what may apply to your situation.',
    ],
    whoFor: [
      'People who need help paying for recovery housing',
      'Families exploring scholarships and assistance for a loved one',
      'Individuals coming from treatment or detox with limited funds',
      'Referral partners asking about funding on a client\u2019s behalf',
    ],
    sections: [
      { h: 'Types of options people ask about', p: [
        'People commonly ask about scholarships, county drug-and-alcohol (SCA) funding, state-related funding, and general payment assistance. Each of these has its own eligibility rules, its own process, and its own availability that can change over time.',
        'From this hub you can explore pages focused on scholarships, county-funded and state-funded options, DDAP-licensed housing, and payment assistance.',
      ] },
      { h: 'The honest next step', p: [
        'Because programs and availability change, the most reliable thing you can do is contact PGSRH and ask what options may currently apply. We\u2019ll give you a straight answer and point you in the right direction.',
        'Approval is never guaranteed, and availability must be confirmed \u2014 but asking costs nothing and often opens doors.',
      ] },
    ],
    faqs: [
      { q: 'Can PGSRH help me pay for recovery housing?', a: 'PGSRH can discuss scholarships, county/state funding, and payment assistance that may be available depending on eligibility and availability. We do not promise free housing or guaranteed approval.' },
      { q: 'What funding options exist?', a: 'People commonly ask about scholarships, county (SCA) funding, state-related funding, and payment assistance. Each has its own requirements and availability.' },
      { q: 'Is any of it guaranteed?', a: 'No. Availability must be confirmed and approval is never guaranteed. Contact PGSRH to ask what may currently apply.' },
      { q: 'How do I start?', a: 'Contact PGSRH and ask about current funding and payment options. We\u2019ll explain what may apply to your situation.' },
    ],
  },
  {
    ...base,
    slug: '/recovery-housing-scholarships-pennsylvania/',
    name: 'Recovery Housing Scholarships Pennsylvania',
    primaryKeyword: 'Recovery Housing Scholarships Pennsylvania',
    keywords: ['PA recovery housing scholarships', 'recovery house scholarship PA', 'sober living scholarships Pennsylvania'],
    title: 'Recovery Housing Scholarships in Pennsylvania | PGSRH',
    description: 'Looking for recovery housing scholarships in Pennsylvania? PGSRH can discuss scholarship options that may be available depending on eligibility and availability. Ask us.',
    blurb: 'Statewide scholarship questions for recovery housing.',
    heroSubtitle: 'Exploring recovery housing scholarships in Pennsylvania? Here\u2019s how to ask about options that may be available.',
    intro: [
      'Many people across Pennsylvania search for recovery housing scholarships to help make a stay affordable. PGSRH can discuss scholarship options that may be available depending on eligibility and current availability.',
      'We reference scholarships generally and honestly \u2014 the specifics depend on the program and your situation, and approval is never guaranteed.',
    ],
    whoFor: [
      'Pennsylvania residents seeking scholarship-supported recovery housing',
      'Families researching scholarships for a loved one',
      'People with limited funds coming from treatment',
      'Referral partners asking about scholarships',
    ],
    sections: [
      { h: 'How scholarships may work', p: [
        'Scholarships for recovery housing can come from various programs, each with its own eligibility requirements and limited availability. They may help offset some or all of a stay for those who qualify.',
        'Because programs change, the best step is to ask PGSRH what may currently apply.',
      ] },
      { h: 'From statewide search to a real home', p: [
        'While scholarship searches are statewide, PGSRH homes are in the Lehigh Valley. If a scholarship option applies, it would support a stay in one of our men\u2019s or women\u2019s homes.',
        'Contact us to ask about scholarships and current availability.',
      ] },
    ],
    faqs: [
      { q: 'Are recovery housing scholarships available in PA?', a: 'Scholarship options may be available depending on eligibility and availability. PGSRH can discuss what may currently apply. Approval is never guaranteed.' },
      { q: 'How do I qualify?', a: 'Eligibility depends on the specific program. Contact PGSRH and we\u2019ll help you understand what may apply.' },
      { q: 'Do scholarships cover the full cost?', a: 'It varies by program and eligibility. Some may offset part or all of a stay for those who qualify; nothing is guaranteed.' },
      { q: 'Where would I stay?', a: 'PGSRH operates men\u2019s and women\u2019s recovery homes in the Lehigh Valley (Whitehall and Catasauqua).' },
    ],
  },
  {
    ...base,
    slug: '/recovery-housing-scholarships-near-me/',
    name: 'Recovery Housing Scholarships Near Me',
    primaryKeyword: 'Recovery Housing Scholarships Near Me',
    keywords: ['recovery house scholarships near me', 'sober living scholarships near me', 'help paying for sober living near me'],
    title: 'Recovery Housing Scholarships Near Me | PGSRH Lehigh Valley',
    description: 'Searching for recovery housing scholarships near you in the Lehigh Valley? PGSRH can discuss scholarship and payment options that may be available. Ask what applies.',
    blurb: 'Local scholarship questions for nearby recovery housing.',
    heroSubtitle: 'Need a nearby recovery home and worried about cost? Here\u2019s how to ask about local scholarship and payment options.',
    intro: [
      'When people search for recovery housing scholarships near them, they usually need something local and affordable at the same time. PGSRH operates homes across the Lehigh Valley and can discuss scholarship or payment options that may be available.',
      'We\u2019ll be honest about what\u2019s possible \u2014 options depend on eligibility and availability, and approval is never guaranteed.',
    ],
    whoFor: [
      'People needing nearby recovery housing plus help with cost',
      'Families searching locally in the Lehigh Valley',
      'Individuals leaving treatment on a tight budget',
      'Anyone asking about scholarships close to home',
    ],
    sections: [
      { h: 'Local homes, honest options', p: [
        'Our men\u2019s homes in Whitehall and our women\u2019s home in Catasauqua keep you close to your support system. If a scholarship or payment option applies, it would support a stay in one of these homes.',
        'Availability of both beds and funding changes, so a quick conversation is the best way to get current answers.',
      ] },
      { h: 'How to ask nearby', p: [
        'Call PGSRH and ask what scholarship or payment options may currently apply in your situation, and what\u2019s open nearby.',
        'For a full overview, visit our funding & scholarship assistance hub.',
      ] },
    ],
    faqs: [
      { q: 'Are there recovery housing scholarships near me?', a: 'Scholarship or payment options may be available depending on eligibility and availability. Contact PGSRH to ask what may currently apply in your area.' },
      { q: 'Where are your nearby homes?', a: 'In Whitehall (men) and Catasauqua (women), all in the Lehigh Valley.' },
      { q: 'Is a scholarship guaranteed?', a: 'No. Availability must be confirmed and approval is never guaranteed.' },
      { q: 'How quickly can I move in?', a: 'Availability changes often. Ask PGSRH about current openings and options.' },
    ],
  },
  {
    ...base,
    slug: '/sober-living-scholarships-pennsylvania/',
    name: 'Sober Living Scholarships Pennsylvania',
    primaryKeyword: 'Sober Living Scholarships Pennsylvania',
    keywords: ['sober living scholarships PA', 'sober house scholarships', 'sober living payment assistance Pennsylvania'],
    title: 'Sober Living Scholarships in Pennsylvania | PGSRH',
    description: 'Searching for sober living scholarships in Pennsylvania? PGSRH can discuss scholarship and payment options that may be available depending on eligibility. Ask what applies.',
    blurb: 'Scholarship questions for those using "sober living" terminology.',
    heroSubtitle: 'Same idea, different words \u2014 here\u2019s how to ask about sober living scholarships and payment help in Pennsylvania.',
    intro: [
      'Some people search for "sober living scholarships" rather than "recovery housing scholarships," but they\u2019re looking for the same thing: help paying for a structured, sober home. PGSRH can discuss scholarship or payment options that may be available.',
      'Sober living and recovery housing mean the same thing here, and the funding questions are the same too.',
    ],
    whoFor: [
      'People who search using "sober living" terminology',
      'Pennsylvania residents seeking help with cost',
      'Families researching scholarships for a loved one',
      'Individuals on a tight budget after treatment',
    ],
    sections: [
      { h: 'Sober living = recovery housing', p: [
        'Because sober living and recovery housing describe the same kind of home, any scholarship or payment options that may apply are the same regardless of which term you use.',
        'What matters is eligibility and availability \u2014 both of which we can help you understand.',
      ] },
      { h: 'Ask about options', p: [
        'Contact PGSRH to ask what scholarship or payment options may currently apply. We\u2019ll explain the possibilities honestly, without guaranteeing approval.',
        'For the full picture, see our recovery housing funding & scholarship assistance hub.',
      ] },
    ],
    faqs: [
      { q: 'Are sober living scholarships different from recovery housing scholarships?', a: 'No. Sober living and recovery housing mean the same thing, so the scholarship and payment questions are the same.' },
      { q: 'What options might be available?', a: 'Scholarships, county/state funding, and payment assistance may be available depending on eligibility and availability. Ask PGSRH what may apply.' },
      { q: 'Is approval guaranteed?', a: 'No. Availability must be confirmed and approval is never guaranteed.' },
      { q: 'Where would I stay?', a: 'PGSRH operates men\u2019s and women\u2019s homes in the Lehigh Valley.' },
    ],
  },
  {
    ...base,
    slug: '/county-funded-recovery-housing-pennsylvania/',
    name: 'County-Funded Recovery Housing Pennsylvania',
    primaryKeyword: 'County-Funded Recovery Housing Pennsylvania',
    keywords: ['county pays for recovery housing', 'county funded sober living PA', 'county drug and alcohol recovery housing'],
    title: 'County-Funded Recovery Housing in Pennsylvania | PGSRH',
    description: 'Wondering if a county program may help pay for recovery housing in PA? County (SCA) funding may be available depending on eligibility and approval. Ask PGSRH what applies.',
    blurb: 'How county (SCA) funding questions may work.',
    heroSubtitle: 'Can a county program help pay for recovery housing? Here\u2019s an honest look at how county (SCA) funding questions work.',
    intro: [
      'Many people hope a county office or county-funded program will pay for recovery housing. In Pennsylvania, county drug-and-alcohol offices (often called SCAs) administer certain funding, and options may be available depending on eligibility, approval, and current program availability.',
      'We use careful language on purpose: county funding is never automatic, and PGSRH cannot promise that a county will pay.',
    ],
    whoFor: [
      'People hoping county funding may cover recovery housing',
      'Families navigating county drug-and-alcohol programs',
      'Individuals referred by a county office or case manager',
      'Referral partners exploring county funding',
    ],
    sections: [
      { h: 'How county funding may work', p: [
        'County (SCA) funding typically depends on eligibility criteria, an approval process, and available program funds, all of which are set by the county \u2014 not by PGSRH. When it applies, it may help with the cost of recovery housing.',
        'Because the county controls approval and availability, we can\u2019t guarantee any outcome.',
      ] },
      { h: 'What PGSRH can do', p: [
        'PGSRH can discuss current payment and referral options and explain how county-related funding questions generally work. If you\u2019re working with a county office or case manager, we\u2019re glad to coordinate.',
        'Contact us to ask what may apply and to talk through next steps.',
      ] },
    ],
    faqs: [
      { q: 'Will the county pay for my recovery housing?', a: 'We can\u2019t promise that. County (SCA) funding may be available depending on eligibility, approval, and program availability, all set by the county. Ask PGSRH what may apply.' },
      { q: 'What is an SCA?', a: 'A Single County Authority is a county drug-and-alcohol office that administers certain funding and referrals in Pennsylvania.' },
      { q: 'How do I find out if I qualify?', a: 'Eligibility is determined by the county. Contact PGSRH and, where relevant, your county office or case manager to explore options.' },
      { q: 'Is county funding guaranteed?', a: 'No. Approval and availability are controlled by the county and are never guaranteed.' },
    ],
  },
  {
    ...base,
    slug: '/state-funded-recovery-housing-pennsylvania/',
    name: 'State-Funded Recovery Housing Pennsylvania',
    primaryKeyword: 'State-Funded Recovery Housing Pennsylvania',
    keywords: ['state funded sober living PA', 'state pays for recovery house', 'Pennsylvania funded recovery housing'],
    title: 'State-Funded Recovery Housing in Pennsylvania | PGSRH',
    description: 'Curious about state-funded recovery housing in PA? Public funding may be available depending on eligibility and program availability. Ask PGSRH what may apply to you.',
    blurb: 'How state or public funding questions may work.',
    heroSubtitle: 'Can state or public funding help with recovery housing in Pennsylvania? Here\u2019s an honest overview.',
    intro: [
      'People often ask whether the state will pay for recovery housing. Public funding in Pennsylvania flows through various programs \u2014 frequently administered at the county level \u2014 and may be available depending on eligibility and program availability.',
      'We\u2019re careful here: PGSRH cannot promise that the state will pay, and availability can change.',
    ],
    whoFor: [
      'People hoping public funding may cover recovery housing',
      'Families researching state-related funding',
      'Individuals referred through public programs',
      'Referral partners exploring funding pathways',
    ],
    sections: [
      { h: 'How public funding may work', p: [
        'State or publicly funded support for recovery housing typically depends on eligibility rules and the availability of program funds. In practice, much of this funding is administered locally through county offices.',
        'When it applies, it may help offset the cost of a stay for those who qualify.',
      ] },
      { h: 'DDAP and honest language', p: [
        'PGSRH operates as DDAP Licensed Recovery Residences. This describes our housing; it does not, by itself, guarantee funding, placement, or eligibility for any program.',
        'Contact PGSRH to ask what public or other funding options may currently apply.',
      ] },
    ],
    faqs: [
      { q: 'Will the state pay for recovery housing?', a: 'We can\u2019t promise that. Public funding may be available depending on eligibility and program availability, often administered locally. Ask PGSRH what may apply.' },
      { q: 'Does DDAP licensing mean funding is guaranteed?', a: 'No. PGSRH operates as DDAP Licensed Recovery Residences, which describes our housing and does not guarantee funding, placement, or eligibility.' },
      { q: 'How is public funding accessed?', a: 'Often through county drug-and-alcohol offices. Contact PGSRH and, where relevant, your county office to explore options.' },
      { q: 'Is it guaranteed?', a: 'No. Availability must be confirmed and approval is never guaranteed.' },
    ],
  },
  {
    ...base,
    slug: '/ddap-licensed-recovery-housing-pennsylvania/',
    name: 'DDAP-Licensed Recovery Housing Pennsylvania',
    primaryKeyword: 'DDAP-Licensed Recovery Housing Pennsylvania',
    keywords: ['DDAP licensed recovery house', 'DDAP recovery housing PA', 'licensed sober living Pennsylvania'],
    title: 'DDAP Licensed Recovery Residences in Pennsylvania | PGSRH',
    description: 'PGSRH operates DDAP Licensed Recovery Residences in the Lehigh Valley, PA \u2014 structured, sober housing for men and women. Learn what this means and how to apply.',
    blurb: 'What DDAP Licensed Recovery Residences means at PGSRH.',
    heroSubtitle: 'PGSRH operates DDAP Licensed Recovery Residences \u2014 here\u2019s what that means, carefully and honestly.',
    fundingHeading: 'Licensing, funding, and honest expectations',
    intro: [
      'PGSRH operates as DDAP Licensed Recovery Residences. Some people specifically search for DDAP-licensed recovery housing, so this page explains what that phrase means in plain terms.',
      'We keep the language precise: this describes our recovery housing. It does not, on its own, guarantee funding, placement, eligibility, or any particular outcome.',
    ],
    whoFor: [
      'People specifically seeking DDAP-licensed recovery housing',
      'Families who want a licensed recovery residence',
      'Referral partners confirming licensing status',
      'Individuals comparing licensed options',
    ],
    sections: [
      { h: 'What this means \u2014 and what it doesn\u2019t', p: [
        'PGSRH operates as DDAP Licensed Recovery Residences. That describes the housing we provide. It is not a statement about funding, and it does not guarantee that any program will pay, that a bed is available, or that anyone will be found eligible for assistance.',
        'We share this language exactly as it appears across our site and avoid expanding it into additional licensing, certification, or approval claims.',
      ] },
      { h: 'The housing we offer', p: [
        'Our focus is structured, substance-free housing with peer support for men and women in the Lehigh Valley. Whether or not funding applies to your situation, our homes provide the same stable environment and clear expectations.',
        'Contact PGSRH to ask about availability and any funding options that may apply.',
      ] },
    ],
    faqs: [
      { q: 'Is PGSRH DDAP licensed?', a: 'PGSRH operates as DDAP Licensed Recovery Residences. This describes our housing and is stated exactly as it appears across our site.' },
      { q: 'Does DDAP licensing guarantee funding or placement?', a: 'No. It describes our recovery housing and does not guarantee funding, placement, eligibility, or any outcome.' },
      { q: 'Does it mean you provide treatment?', a: 'No. PGSRH provides recovery housing only \u2014 not detox, rehab, therapy, or medical services.' },
      { q: 'How do I apply?', a: 'Apply online or call PGSRH to ask about availability and any funding options that may apply.' },
    ],
  },
  {
    ...base,
    slug: '/recovery-housing-payment-assistance/',
    name: 'Recovery Housing Payment Assistance',
    primaryKeyword: 'Recovery Housing Payment Assistance',
    keywords: ['help paying for recovery housing', 'recovery house payment help', 'sober living financial assistance'],
    title: 'Recovery Housing Payment Assistance | PGSRH Lehigh Valley, PA',
    description: 'Need help paying for recovery housing? PGSRH can discuss payment assistance and funding options that may be available depending on eligibility. Ask about funding options.',
    blurb: 'For people who need help paying and are ready to ask.',
    heroSubtitle: 'If you\u2019re ready to move forward but worried about paying, let\u2019s talk about the options that may help.',
    intro: [
      'This page is for people who are ready to take the step into recovery housing but need help with cost. PGSRH can discuss payment assistance and funding options that may be available depending on eligibility and current availability.',
      'We won\u2019t promise free housing, but we will help you understand what may be possible and how to ask.',
    ],
    whoFor: [
      'People ready to apply who need help paying',
      'Families arranging payment for a loved one',
      'Individuals leaving treatment with limited funds',
      'Anyone who needs to understand payment options',
    ],
    sections: [
      { h: 'Options people ask about', p: [
        'Payment assistance can take different forms \u2014 scholarships, county or state-related funding, or other arrangements \u2014 each with its own eligibility and availability. What applies depends on your situation.',
        'The fastest way to get answers is a direct conversation about your circumstances.',
      ] },
      { h: 'Ask about funding options', p: [
        'Contact PGSRH and ask what payment or funding options may currently apply. We\u2019ll be straightforward about what\u2019s possible and what isn\u2019t.',
        'For a full overview, visit our funding & scholarship assistance hub.',
      ] },
    ],
    faqs: [
      { q: 'Can I get help paying for recovery housing?', a: 'Payment assistance and funding options may be available depending on eligibility and availability. Ask PGSRH about funding options; approval is never guaranteed.' },
      { q: 'What forms can assistance take?', a: 'Scholarships, county/state-related funding, or other arrangements \u2014 each with its own requirements. What applies depends on your situation.' },
      { q: 'Is it free?', a: 'We don\u2019t promise free housing. Assistance may help offset cost for those who qualify.' },
      { q: 'How do I ask?', a: 'Contact PGSRH directly and ask about current payment and funding options.' },
    ],
  },
  {
    ...base,
    slug: '/affordable-recovery-housing-scholarships/',
    name: 'Affordable Recovery Housing with Scholarships',
    primaryKeyword: 'Affordable Recovery Housing with Scholarships',
    keywords: ['affordable recovery housing', 'low cost sober living', 'recovery housing scholarships', 'affordable sober living PA'],
    title: 'Affordable Recovery Housing with Scholarships | PGSRH',
    description: 'Affordable recovery housing may be within reach. PGSRH can discuss scholarship and funding options that may help depending on eligibility and availability. Ask what applies.',
    blurb: 'Affordability tied directly to scholarship and funding options.',
    heroSubtitle: 'Affordable recovery housing and scholarships \u2014 how to bring cost and support together.',
    intro: [
      'This page connects two things people often search for together: affordable recovery housing and scholarships. PGSRH can discuss scholarship and funding options that may help make a stay more affordable, depending on eligibility and availability.',
      'We\u2019re honest about limits \u2014 no guarantees of free housing \u2014 while helping you understand what may be possible.',
    ],
    whoFor: [
      'People searching for affordable recovery housing',
      'Individuals looking for scholarship-supported stays',
      'Families on a budget researching options',
      'Anyone combining affordability and scholarship searches',
    ],
    sections: [
      { h: 'Affordability and scholarships together', p: [
        'For those who qualify, a scholarship or other funding may reduce the out-of-pocket cost of recovery housing. Affordability then becomes a combination of the base cost and any assistance that applies.',
        'Because programs and availability change, a direct conversation gives the clearest picture.',
      ] },
      { h: 'How to ask', p: [
        'Contact PGSRH to ask what scholarship or funding options may currently apply and what a stay would involve.',
        'See our funding & scholarship assistance hub for the full overview.',
      ] },
    ],
    faqs: [
      { q: 'Can scholarships make recovery housing affordable?', a: 'For those who qualify, scholarships or other funding may reduce cost. Availability and eligibility vary and approval is never guaranteed.' },
      { q: 'Is there free recovery housing?', a: 'We don\u2019t promise free housing. Assistance may help offset cost for eligible individuals.' },
      { q: 'How do I find out what applies?', a: 'Contact PGSRH and ask about current scholarship and funding options.' },
      { q: 'Where would I stay?', a: 'PGSRH operates men\u2019s and women\u2019s recovery homes in the Lehigh Valley.' },
    ],
  },
  {
    ...base,
    slug: '/recovery-housing-grants-scholarships/',
    name: 'Recovery Housing Grants and Scholarships',
    primaryKeyword: 'Recovery Housing Grants and Scholarships',
    keywords: ['recovery house grants', 'sober living grants', 'recovery housing grant', 'scholarship for recovery house'],
    title: 'Recovery Housing Grants and Scholarships | PGSRH',
    description: 'Searching for recovery housing grants or scholarships? Learn how grants and scholarships differ and how PGSRH can discuss options that may apply. Ask about funding options.',
    blurb: 'How grants and scholarships differ for recovery housing.',
    heroSubtitle: 'Grants vs. scholarships \u2014 what\u2019s the difference, and what might help you pay for recovery housing?',
    intro: [
      'Some people search for recovery housing "grants," while others search for "scholarships." This page explains the difference and how PGSRH can discuss options that may apply to your situation.',
      'The short version: grants often fund organizations and programs, while individuals more often benefit from scholarships or payment assistance.',
    ],
    whoFor: [
      'People using "grant" terminology in their search',
      'Individuals looking for any help with cost',
      'Families researching funding for a loved one',
      'Referral partners exploring options',
    ],
    sections: [
      { h: 'Grants vs. scholarships', p: [
        'Grants are typically awarded to organizations or programs to fund services. Scholarships and payment assistance are more often what help an individual afford a stay in recovery housing.',
        'Whichever term you started with, the practical question is the same: what may help you pay?',
      ] },
      { h: 'What may apply to you', p: [
        'PGSRH can discuss scholarship and payment options that may be available depending on eligibility and availability. Approval is never guaranteed.',
        'Contact us to ask what may apply, or visit our funding & scholarship assistance hub.',
      ] },
    ],
    faqs: [
      { q: 'What\u2019s the difference between a grant and a scholarship?', a: 'Grants usually fund organizations or programs; scholarships and payment assistance more often help individuals afford a stay.' },
      { q: 'Can I get a grant to pay for recovery housing myself?', a: 'Individuals more commonly benefit from scholarships or payment assistance. PGSRH can discuss what may apply to you.' },
      { q: 'Is funding guaranteed?', a: 'No. Availability and eligibility vary and approval is never guaranteed.' },
      { q: 'How do I ask?', a: 'Contact PGSRH to ask about current scholarship and payment options.' },
    ],
  },
  {
    ...base,
    slug: '/recovery-housing-after-rehab-financial-assistance/',
    name: 'Recovery Housing After Rehab with Financial Assistance',
    primaryKeyword: 'Recovery Housing After Rehab Financial Assistance',
    keywords: ['housing after rehab with scholarship', 'recovery housing after treatment funding', 'sober living after rehab payment help'],
    title: 'Recovery Housing After Rehab with Financial Assistance | PGSRH',
    description: 'Leaving rehab and worried about cost? PGSRH can discuss financial assistance and funding that may help with recovery housing after treatment. Ask what may apply.',
    blurb: 'Funding help for the step from rehab into recovery housing.',
    heroSubtitle: 'Stepping down from treatment and worried about paying? Here\u2019s how to ask about financial assistance for recovery housing after rehab.',
    intro: [
      'The transition from rehab to recovery housing is a critical moment \u2014 and cost shouldn\u2019t derail it. PGSRH can discuss financial assistance and funding options that may help with recovery housing after treatment, depending on eligibility and availability.',
      'This page pairs discharge planning with honest funding information so families and providers can plan a smooth step.',
    ],
    whoFor: [
      'People leaving rehab who need help paying for housing',
      'Treatment teams planning a funded discharge',
      'Families arranging a next step within a budget',
      'Referral partners coordinating funding and placement',
    ],
    sections: [
      { h: 'Plan the transition and the funding together', p: [
        'When someone leaves treatment, timing matters \u2014 both for the move-in and for any funding that may apply. Coordinating early gives the best chance of a seamless step from rehab into recovery housing.',
        'PGSRH provides housing, not treatment; clinical care continues with a resident\u2019s own providers.',
      ] },
      { h: 'Ask early', p: [
        'Contact PGSRH as discharge approaches to ask about availability and what financial assistance or funding options may apply.',
        'See our housing-after-rehab and funding hub pages for more.',
      ] },
    ],
    faqs: [
      { q: 'Is financial assistance available for housing after rehab?', a: 'Financial assistance or funding options may be available depending on eligibility and availability. Ask PGSRH what may apply; approval is not guaranteed.' },
      { q: 'When should I ask about funding?', a: 'As early as possible before discharge, so timing and any funding can be coordinated.' },
      { q: 'Does PGSRH provide the rehab or aftercare?', a: 'No. PGSRH provides recovery housing only. Clinical care continues with your own providers.' },
      { q: 'Can my treatment team help arrange this?', a: 'Yes. Treatment teams and case managers can contact PGSRH to coordinate placement and funding questions.' },
    ],
  },
  {
    ...base,
    slug: '/recovery-housing-after-detox-financial-assistance/',
    name: 'Recovery Housing After Detox with Financial Assistance',
    primaryKeyword: 'Recovery Housing After Detox Financial Assistance',
    keywords: ['housing after detox with scholarship', 'sober living after detox funding', 'recovery house after detox payment help'],
    title: 'Recovery Housing After Detox with Financial Assistance | PGSRH',
    description: 'Completing detox and need help paying for housing? PGSRH can discuss financial assistance and funding that may help with recovery housing after detox. Ask what applies.',
    blurb: 'Funding help for the step from detox into recovery housing.',
    heroSubtitle: 'Just completed detox and worried about cost? Here\u2019s how to ask about financial assistance for recovery housing after detox.',
    intro: [
      'The days right after detox are urgent, and cost can feel like one more barrier. PGSRH can discuss financial assistance and funding that may help with recovery housing after detox, depending on eligibility and availability.',
      'To be clear, PGSRH does not provide detox or medical services \u2014 we provide the sober housing that comes next, and we\u2019ll talk honestly about funding.',
    ],
    whoFor: [
      'People completing detox who need help paying for housing',
      'Detox and treatment teams planning a funded next step',
      'Families searching urgently after detox',
      'Referral partners coordinating funding and placement',
    ],
    sections: [
      { h: 'Move quickly, ask early', p: [
        'Because the post-detox window is fragile, coordinating both a move-in and any funding as early as possible gives the smoothest transition into a stable, sober home.',
        'We can work with detox and treatment teams on timing.',
      ] },
      { h: 'Housing \u2014 not detox', p: [
        'PGSRH provides recovery housing for after detox is complete. We do not provide detox or medical care. Any clinical needs continue with a resident\u2019s own providers.',
        'Contact us to ask about availability and what financial assistance may apply.',
      ] },
    ],
    faqs: [
      { q: 'Is help with cost available after detox?', a: 'Financial assistance or funding options may be available depending on eligibility and availability. Ask PGSRH what may apply; approval is not guaranteed.' },
      { q: 'Does PGSRH provide detox?', a: 'No. PGSRH provides recovery housing for after detox is completed elsewhere. We do not provide detox or medical services.' },
      { q: 'How fast can this be arranged?', a: 'Availability changes, and the post-detox window is time-sensitive. Contact PGSRH early to coordinate.' },
      { q: 'Can a detox team refer me?', a: 'Yes. Detox and treatment teams can contact PGSRH to coordinate placement and funding questions.' },
    ],
  },
  {
    ...base,
    slug: '/mens-recovery-housing-scholarships-pennsylvania/',
    gender: 'men',
    name: "Men's Recovery Housing Scholarships Pennsylvania",
    primaryKeyword: "Men's Recovery Housing Scholarships Pennsylvania",
    keywords: ["men's sober living scholarships PA", 'male recovery house scholarship', "men's recovery housing funding"],
    title: "Men's Recovery Housing Scholarships in Pennsylvania | PGSRH",
    description: "Looking for men's recovery housing scholarships in PA? PGSRH can discuss scholarship and funding options that may apply to our men's home in Whitehall.",
    blurb: "Scholarship questions for men's recovery housing.",
    heroSubtitle: 'Scholarship and funding questions for men\u2019s recovery housing in Pennsylvania \u2014 tied to our Whitehall home.',
    intro: [
      'This page is for men (and their families) who need scholarship or funding help with recovery housing in Pennsylvania. PGSRH can discuss options that may be available depending on eligibility and availability, tied to our men\u2019s homes in Whitehall.',
      'As always, we won\u2019t promise free housing or guaranteed approval \u2014 we\u2019ll help you understand what may apply.',
    ],
    whoFor: [
      'Men seeking scholarship-supported recovery housing in PA',
      'Families researching funding for a son or brother',
      'Men leaving treatment on a tight budget',
      'Referral partners placing male clients with funding needs',
    ],
    sections: [
      { h: 'Funding for men\u2019s homes', p: [
        'If a scholarship or funding option applies, it would support a stay in one of our men\u2019s homes in Whitehall. Eligibility and availability vary by program.',
        'Our men\u2019s hub and city pages explain the homes themselves.',
      ] },
      { h: 'Ask what applies', p: [
        'Contact PGSRH to ask what scholarship or funding options may currently apply for men\u2019s recovery housing, and what\u2019s open.',
        'See our funding & scholarship assistance hub for the full overview.',
      ] },
    ],
    faqs: [
      { q: 'Are men\u2019s recovery housing scholarships available in PA?', a: 'Scholarship or funding options may be available depending on eligibility and availability. Ask PGSRH what may apply; approval is never guaranteed.' },
      { q: 'Which homes would a scholarship support?', a: 'Our men\u2019s homes in Whitehall, PA.' },
      { q: 'How do I qualify?', a: 'Eligibility depends on the specific program. Contact PGSRH to explore what may apply.' },
      { q: 'Is it guaranteed?', a: 'No. Availability must be confirmed and approval is never guaranteed.' },
    ],
  },
  {
    ...base,
    slug: '/womens-recovery-housing-scholarships-pennsylvania/',
    gender: 'women',
    name: "Women's Recovery Housing Scholarships Pennsylvania",
    primaryKeyword: "Women's Recovery Housing Scholarships Pennsylvania",
    keywords: ["women's sober living scholarships PA", 'female recovery house scholarship', "women's recovery housing funding"],
    title: "Women's Recovery Housing Scholarships in Pennsylvania | PGSRH",
    description: "Looking for women's recovery housing scholarships in PA? PGSRH can discuss scholarship and funding options that may apply to our women's home in Catasauqua.",
    blurb: "Scholarship questions for women's recovery housing.",
    heroSubtitle: 'Scholarship and funding questions for women\u2019s recovery housing in Pennsylvania \u2014 tied to our Catasauqua home.',
    intro: [
      'This page is for women (and their families) who need scholarship or funding help with recovery housing in Pennsylvania. PGSRH can discuss options that may be available depending on eligibility and availability, tied to our women\u2019s home in Catasauqua.',
      'We won\u2019t promise free housing or guaranteed approval \u2014 we\u2019ll help you understand what may apply.',
    ],
    whoFor: [
      'Women seeking scholarship-supported recovery housing in PA',
      'Families researching funding for a daughter or sister',
      'Women leaving treatment on a tight budget',
      'Referral partners placing female clients with funding needs',
    ],
    sections: [
      { h: 'Funding for the women\u2019s home', p: [
        'If a scholarship or funding option applies, it would support a stay in our women\u2019s home in Catasauqua. Eligibility and availability vary by program.',
        'Our women\u2019s hub and Catasauqua page explain the home itself.',
      ] },
      { h: 'Ask what applies', p: [
        'Contact PGSRH to ask what scholarship or funding options may currently apply for women\u2019s recovery housing, and what\u2019s open.',
        'See our funding & scholarship assistance hub for the full overview.',
      ] },
    ],
    faqs: [
      { q: 'Are women\u2019s recovery housing scholarships available in PA?', a: 'Scholarship or funding options may be available depending on eligibility and availability. Ask PGSRH what may apply; approval is never guaranteed.' },
      { q: 'Which home would a scholarship support?', a: 'Our women\u2019s home in Catasauqua, PA (425 Walnut Street).' },
      { q: 'How do I qualify?', a: 'Eligibility depends on the specific program. Contact PGSRH to explore what may apply.' },
      { q: 'Is it guaranteed?', a: 'No. Availability must be confirmed and approval is never guaranteed.' },
    ],
  },
  {
    ...base,
    slug: '/recovery-housing-scholarships-lehigh-valley-pa/',
    name: 'Recovery Housing Scholarships Lehigh Valley PA',
    primaryKeyword: 'Recovery Housing Scholarships Lehigh Valley PA',
    keywords: ['sober living scholarships Lehigh Valley PA', 'recovery house scholarship Lehigh Valley', 'recovery housing funding Lehigh Valley'],
    title: 'Recovery Housing Scholarships in the Lehigh Valley, PA | PGSRH',
    description: 'Searching for recovery housing scholarships in the Lehigh Valley? PGSRH can discuss scholarship and funding options that may apply to our local homes. Ask what applies.',
    blurb: 'Regional scholarship questions across the Lehigh Valley.',
    heroSubtitle: 'Scholarship and funding questions for recovery housing right here in the Lehigh Valley.',
    intro: [
      'This regional page connects scholarship searches to PGSRH\u2019s actual homes in the Lehigh Valley. We can discuss scholarship and funding options that may be available depending on eligibility and availability.',
      'If an option applies, it would support a stay in Whitehall or Catasauqua.',
    ],
    whoFor: [
      'Lehigh Valley residents seeking help with cost',
      'Families researching local scholarships',
      'People leaving treatment who want to stay local',
      'Referral partners placing local clients',
    ],
    sections: [
      { h: 'Local homes, local options', p: [
        'Our two homes keep residents close to Lehigh Valley meetings, jobs, and family. Any scholarship or funding that applies would support a stay in one of them.',
        'Both bed and funding availability change, so a quick call gives the clearest answer.',
      ] },
      { h: 'Ask about options', p: [
        'Contact PGSRH to ask what scholarship or funding options may currently apply locally, and what\u2019s open.',
        'See our funding hub and county-funded Lehigh Valley page for more.',
      ] },
    ],
    faqs: [
      { q: 'Are scholarships available in the Lehigh Valley?', a: 'Scholarship or funding options may be available depending on eligibility and availability. Ask PGSRH what may apply; approval is never guaranteed.' },
      { q: 'Which homes would be supported?', a: 'Our homes in Whitehall and Catasauqua.' },
      { q: 'Is it guaranteed?', a: 'No. Availability must be confirmed and approval is never guaranteed.' },
      { q: 'How do I ask?', a: 'Contact PGSRH to ask about current scholarship and funding options in the Lehigh Valley.' },
    ],
  },
  {
    ...base,
    slug: '/county-funded-recovery-housing-lehigh-valley-pa/',
    name: 'County-Funded Recovery Housing Lehigh Valley PA',
    primaryKeyword: 'County-Funded Recovery Housing Lehigh Valley PA',
    keywords: ['county funded sober living Lehigh Valley', 'county pays for recovery house Lehigh Valley', 'SCA recovery housing Lehigh Valley'],
    title: 'County-Funded Recovery Housing in the Lehigh Valley, PA | PGSRH',
    description: 'Wondering about county (SCA) funding for recovery housing in the Lehigh Valley? Options may be available depending on eligibility and approval. Ask PGSRH what applies.',
    blurb: 'County (SCA) funding questions for the Lehigh Valley.',
    heroSubtitle: 'County (SCA) funding questions for recovery housing in the Lehigh Valley \u2014 an honest, local overview.',
    intro: [
      'People in the Lehigh Valley often ask whether county funding may help pay for recovery housing. County drug-and-alcohol offices (SCAs) administer certain funding, and options may be available depending on eligibility, approval, and program availability.',
      'We use careful language: county funding is never automatic, and PGSRH cannot promise a county will pay.',
    ],
    whoFor: [
      'Lehigh Valley residents exploring county funding',
      'Families navigating county drug-and-alcohol programs',
      'People referred by a county office or case manager',
      'Referral partners exploring local funding',
    ],
    sections: [
      { h: 'How county funding may work locally', p: [
        'County (SCA) funding depends on eligibility criteria, an approval process, and available funds \u2014 all set by the county, not by PGSRH. When it applies, it may help with the cost of recovery housing in the Lehigh Valley.',
        'Because the county controls approval and availability, we can\u2019t guarantee any outcome.',
      ] },
      { h: 'What PGSRH can do', p: [
        'PGSRH can discuss current payment and referral options and explain how county-related funding generally works for our local homes. If you\u2019re working with a county office or case manager, we\u2019re glad to coordinate.',
        'Contact us to ask what may apply.',
      ] },
    ],
    faqs: [
      { q: 'Will the county pay for recovery housing in the Lehigh Valley?', a: 'We can\u2019t promise that. County (SCA) funding may be available depending on eligibility, approval, and availability, all set by the county. Ask PGSRH what may apply.' },
      { q: 'What is an SCA?', a: 'A Single County Authority is a county drug-and-alcohol office that administers certain funding and referrals.' },
      { q: 'Which homes would this support?', a: 'Our Lehigh Valley homes in Whitehall and Catasauqua.' },
      { q: 'Is it guaranteed?', a: 'No. Approval and availability are controlled by the county and are never guaranteed.' },
    ],
  },
  {
    ...base,
    slug: '/recovery-housing-scholarships-whitehall-pa/',
    gender: 'men', city: 'Whitehall',
    name: 'Recovery Housing Scholarships Whitehall PA',
    primaryKeyword: 'Recovery Housing Scholarships Whitehall PA',
    keywords: ['Whitehall PA recovery house scholarship', 'sober living scholarships Whitehall PA', 'funded recovery housing Whitehall'],
    title: 'Recovery Housing Scholarships in Whitehall, PA | PGSRH',
    description: 'Scholarship and funding questions for recovery housing in Whitehall, PA. PGSRH can discuss options that may apply to our men\u2019s home. Ask about funding options.',
    blurb: 'City-specific scholarship questions for the Whitehall men\u2019s home.',
    heroSubtitle: 'Scholarship and funding questions for our men\u2019s recovery home in Whitehall, PA.',
    intro: [
      'This city page is for men (and families) asking about scholarships or funding for recovery housing in Whitehall. PGSRH can discuss options that may be available depending on eligibility and availability for our Whitehall men\u2019s home.',
      'No promises of free housing or guaranteed approval \u2014 just honest guidance on what may apply.',
    ],
    whoFor: [
      'Men seeking funded recovery housing in Whitehall',
      'Families researching scholarships locally',
      'Men leaving treatment who want to stay in the area',
      'Referral partners placing local male clients',
    ],
    sections: [
      { h: 'Funding for the Whitehall home', p: [
        'If a scholarship or funding option applies, it would support a stay at our men\u2019s home at 855 3rd Street, Whitehall, PA 18052. Eligibility and availability vary by program.',
        'Our Whitehall page explains the home itself.',
      ] },
      { h: 'Ask what applies', p: [
        'Contact PGSRH to ask what scholarship or funding options may currently apply for the Whitehall house, and what\u2019s open.',
        'See our funding hub for the full overview.',
      ] },
    ],
    faqs: [
      { q: 'Are scholarships available for the Whitehall home?', a: 'Scholarship or funding options may be available depending on eligibility and availability. Ask PGSRH what may apply; approval is never guaranteed.' },
      { q: 'Where is the Whitehall home?', a: 'At 855 3rd Street, Whitehall, PA 18052 \u2014 a men\u2019s recovery home.' },
      { q: 'Is it guaranteed?', a: 'No. Availability must be confirmed and approval is never guaranteed.' },
      { q: 'How do I ask?', a: 'Call 570-579-7695 or contact PGSRH to ask about current options.' },
    ],
  },
  {
    ...base,
    slug: '/recovery-housing-scholarships-catasauqua-pa/',
    gender: 'women', city: 'Catasauqua',
    name: 'Recovery Housing Scholarships Catasauqua PA',
    primaryKeyword: 'Recovery Housing Scholarships Catasauqua PA',
    keywords: ['Catasauqua PA recovery house scholarship', 'sober living scholarships Catasauqua PA', 'funded recovery housing Catasauqua'],
    title: 'Recovery Housing Scholarships in Catasauqua, PA | PGSRH',
    description: 'Scholarship and funding questions for recovery housing in Catasauqua, PA. PGSRH can discuss options that may apply to our women\u2019s home. Ask about funding options.',
    blurb: 'City-specific scholarship questions for the Catasauqua women\u2019s home.',
    heroSubtitle: 'Scholarship and funding questions for our women\u2019s recovery home in Catasauqua, PA.',
    intro: [
      'This city page is for women (and families) asking about scholarships or funding for recovery housing in Catasauqua. PGSRH can discuss options that may be available depending on eligibility and availability for our Catasauqua women\u2019s home.',
      'No promises of free housing or guaranteed approval \u2014 just honest guidance on what may apply.',
    ],
    whoFor: [
      'Women seeking funded recovery housing in Catasauqua',
      'Families researching scholarships locally',
      'Women leaving treatment who want to stay in the area',
      'Referral partners placing local female clients',
    ],
    sections: [
      { h: 'Funding for the Catasauqua home', p: [
        'If a scholarship or funding option applies, it would support a stay at our women\u2019s home at 425 Walnut Street, Catasauqua, PA 18032. Eligibility and availability vary by program.',
        'Our Catasauqua page explains the home itself.',
      ] },
      { h: 'Ask what applies', p: [
        'Contact PGSRH to ask what scholarship or funding options may currently apply for the Catasauqua house, and what\u2019s open.',
        'See our funding hub for the full overview.',
      ] },
    ],
    faqs: [
      { q: 'Are scholarships available for the Catasauqua home?', a: 'Scholarship or funding options may be available depending on eligibility and availability. Ask PGSRH what may apply; approval is never guaranteed.' },
      { q: 'Where is the Catasauqua home?', a: 'At 425 Walnut Street, Catasauqua, PA 18032 \u2014 a women\u2019s recovery home.' },
      { q: 'Is it guaranteed?', a: 'No. Availability must be confirmed and approval is never guaranteed.' },
      { q: 'How do I ask?', a: 'Call 610-443-0175 or contact PGSRH to ask about current options.' },
    ],
  },
  {
    ...base,
    slug: '/recovery-housing-county-assistance/',
    name: 'Recovery Housing Through County Assistance',
    primaryKeyword: 'Recovery Housing County Assistance',
    keywords: ['county assistance recovery housing', 'county drug and alcohol office recovery housing', 'SCA recovery housing assistance'],
    title: 'Recovery Housing Through County Assistance | PGSRH',
    description: 'How might county assistance or referrals help with recovery housing? Learn how county (SCA) processes generally work and how to ask PGSRH what options may apply.',
    blurb: 'How county assistance and referrals may work.',
    heroSubtitle: 'How county assistance and referrals may connect to recovery housing \u2014 and how to start the conversation.',
    intro: [
      'This educational page explains, in general terms, how county assistance or referrals may relate to recovery housing \u2014 and how to contact PGSRH to ask about options. County drug-and-alcohol offices (SCAs) administer certain funding and referrals in Pennsylvania.',
      'We keep expectations honest: county assistance depends on eligibility, approval, and availability, and is never guaranteed.',
    ],
    whoFor: [
      'People exploring county assistance for recovery housing',
      'Families working with a county drug-and-alcohol office',
      'Individuals referred through county programs',
      'Referral partners coordinating county processes',
    ],
    sections: [
      { h: 'How county assistance generally works', p: [
        'A county drug-and-alcohol office may assess eligibility, make referrals, and administer certain funding. These processes are set by the county, and they determine approval and availability \u2014 not PGSRH.',
        'When assistance applies, it may help connect a person to recovery housing and, in some cases, help with cost.',
      ] },
      { h: 'Starting the conversation', p: [
        'If you\u2019re working with a county office or case manager, PGSRH is glad to coordinate. We can explain how our homes work and discuss current payment and referral options.',
        'Contact us to ask what may apply and to talk through next steps.',
      ] },
    ],
    faqs: [
      { q: 'Can county assistance connect me to recovery housing?', a: 'County (SCA) processes may assess eligibility, make referrals, and administer certain funding. Options depend on the county\u2019s rules and availability, and are never guaranteed.' },
      { q: 'Does PGSRH control county approval?', a: 'No. Approval and availability are set by the county. PGSRH can coordinate and discuss current options.' },
      { q: 'How do I start?', a: 'Contact PGSRH and, where relevant, your county drug-and-alcohol office or case manager.' },
      { q: 'Is assistance guaranteed?', a: 'No. Eligibility, approval, and availability are controlled by the county and are never guaranteed.' },
    ],
  },
];

export default pages;
